/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  HostBinding,
  HostListener,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import orderBy from 'lodash-es/orderBy';

import * as moment from 'moment/moment';

import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {Employee} from '../../../shared/employees/employees.model';
import {ZoneType} from '../../employees/contract/contract.interfaces';
import {ModalService} from '../../modal';
import {SortKey} from '../../ui/table/table.interfaces';
import {groupBy} from '../../utils/arrays';
import {isValueConsideredAsEmpty} from '../../utils/lang';
import {Strings} from '../../utils/strings';
import {
  BlockingError,
  ErrorMessage,
  FieldEventTrace,
  FormControlWithNativeElement,
  FormGeneratorError,
  FormGeneratorErrorKind,
  FormValues,
  GroupZoneDescriptor,
  GroupZoneType,
  ZoneDescriptor
} from '../form-generator.interfaces';
import {FormGeneratorService} from '../form-generator.service';
import {FormGeneratorUtils} from '../form-generator.utils';

import {OrgEntity} from '../../../+organizations/org-interfaces';
import {FormTableRowComponent} from './form-table-row/form-table-row.component';

/**
 * Table component for the form generator.
 * Holds a groupZone of table type
 */
@Component({
  selector: '[gp-form-table]',
  templateUrl: 'form-table.component.html',
  styleUrls: ['./form-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class FormTableComponent extends Destroyable implements OnDestroy {

  public loaded: EventEmitter<UntypedFormArray> = new EventEmitter<UntypedFormArray>();
  public descriptor: GroupZoneDescriptor;
  public tabSheetID: string;
  public tableRows: Array<ComponentRef<FormTableRowComponent>> = [];
  public data: any;
  public lastNonBlockingData: any;
  public dataIndex: number;
  public readOnly: boolean = false;
  public locale: string;
  public currency: string;

  public sortKey: any;
  public selectKey: boolean = false;
  public isFirstLoad: boolean = true;

  @ViewChild('tableRowsContainer', {read: ViewContainerRef, static: true})
  public tableRowsContainer: ViewContainerRef;

  public isHover: boolean;
  public group: UntypedFormArray;

  public errors: FormGeneratorError;
  public errorText: string;
  public errorMessages: ErrorMessage[] = [];

  public zoneErrors: FormGeneratorError;
  public zoneErrorMessages: ErrorMessage[] = [];

  public isNewTableRow: boolean = false;
  public form: UntypedFormGroup;
  public requiredZones: any[] = [];
  public initialGroupValue: any;
  public groupValueChangesSubscription: Subscription;

  public tabChangeSubscription: Subscription;
  public fieldFocusedSubscription: Subscription;
  public fieldBlurredSubscription: Subscription;
  public lastFieldFocused: FieldEventTrace;
  public lastFieldBlurred: FieldEventTrace;

  // Keep track from blocking errors at load and whenever there is a blur/focus
  public lastBlockingErrors: BlockingError[] = [];
  // Blocking errors after a field is blurred
  public newBlockingErrors: BlockingError[] = [];

  public blockingErrorModalIsLaunched = false;
  public isOnBoarding: boolean = false;
  public employee: Employee = null;
  public selectedOrg: OrgEntity = null;
  public allData: FormValues;

  @HostListener('mouseenter')
  public onMouseEnter() {
    this.isHover = true;
  }

  @HostListener('mouseleave')
  public onMouseLeave() {
    this.isHover = false;
  }

  @HostBinding('style.display')
  public get position() {
    return 'flex';
  }

  @HostBinding('class.col-xs-12')
  @HostBinding('class.col-md-3')
  public get is3Col() {
    return this.descriptor && this.descriptor.nbCol === 1;
  }

  @HostBinding('class.col-xs-12')
  @HostBinding('class.col-md-6')
  public get is6Col() {
    return this.descriptor && this.descriptor.nbCol === 2;
  }

  @HostBinding('class.col-xs-12')
  @HostBinding('class.col-md-9')
  public get is9Col() {
    return this.descriptor && this.descriptor.nbCol === 3;
  }

  @HostBinding('class.col-xs-12')
  @HostBinding('class.col-md-12')
  public get is12Col() {
    return this.descriptor && this.descriptor.nbCol === 4;
  }

  @HostBinding('class.is-active')
  public get isActiveCard(): boolean {
    return this.descriptor.isFocused || this.isHover;
  }

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private changeDetectorRef: ChangeDetectorRef,
              private formGeneratorService: FormGeneratorService,
              private formBuilder: UntypedFormBuilder,
              private modalService: ModalService,
              private translate: TranslateService) {
    super();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    if (this.groupValueChangesSubscription) {
      this.groupValueChangesSubscription.unsubscribe();
    }
    if (this.tabChangeSubscription) {
      this.tabChangeSubscription.unsubscribe();
      this.tabChangeSubscription = undefined;
    }
    this.removeFieldEventsListeners();
    this.tableRows.forEach(ref => {
      ref.destroy();
      ref = null;
    });
    this.tableRows = [];
  }

  public onSortKeyChange(key: SortKey) {
    this.sortKey = key;
    this.sort();
    this.loadRows();
  }

  public init() {

    if (this.descriptor.readOnly) {
      this.readOnly = true;
    }
    this.descriptor.table = this;

    this.descriptor.zones = this.descriptor.zones
      .sort((zoneA: ZoneDescriptor, zoneB: ZoneDescriptor) => zoneA.displayorder - zoneB.displayorder);

    this.group = this.formBuilder.array([]);
    this.group.setValidators(this.timeLineValidation.bind(this));
    this.descriptor.zones.forEach(zone => {
      if (zone.isRequired && zone.type !== ZoneType.AUTO_GENERATED) {
        this.requiredZones.push({zoneID: zone.zoneID, transId: zone.transId});
      }
    });
    this.translate.onLangChange.asObservable().subscribe(() => this.timeLineValidation());
    this.loadRows()
      .then(groupZonesControlGroup => {
        this.loaded.emit(groupZonesControlGroup);
        this.loaded.complete();
        this.initialGroupValue = cloneDeep(this.group.value);
        this.lastBlockingErrors = this.getBlockingErrors();
      });

    this.changeDetectorRef.detectChanges();
  }

  public handleFocusEvent(event: FieldEventTrace) {
    if (!this.formGeneratorService.isSameFieldThanPreviousAndHasError(
      event,
      this.lastFieldFocused,
      this.newBlockingErrors,
      this.dataIndex)
    ) {
      if (this.blockingErrorModalIsLaunched) {
        const currentField: FormControlWithNativeElement =
          this.formGeneratorService.getZoneControlById(event.descriptor.zoneID, this.group, this.dataIndex);
        currentField.nativeElement.blur();
        return;
      }
      this.lastFieldFocused = cloneDeep(event);
      this.lastFieldFocused.dataIndex = this.dataIndex;
    }
    this.lastBlockingErrors = this.getBlockingErrors();
    this.changeDetectorRef.detectChanges();
  }

  public handleBlurEvent(event: FieldEventTrace) {
    if (this.blockingErrorModalIsLaunched) {
      return;
    }
    let openModal: boolean;

    // In case we did backToEditMode, we check if error is still there
    if (this.formGeneratorService.isSameFieldThanPreviousAndHasError(
      event,
      this.lastFieldBlurred,
      this.newBlockingErrors,
      this.dataIndex)
    ) {
      const previousErrorLength = this.newBlockingErrors.length;
      this.lastBlockingErrors = this.getBlockingErrors();
      this.changeDetectorRef.detectChanges();
      openModal = this.lastBlockingErrors.length === previousErrorLength;
    } else {
      // standard case, we check new blocking errors
      this.lastFieldBlurred = cloneDeep(event);
      this.lastFieldBlurred.dataIndex = this.dataIndex;

      this.newBlockingErrors = this.getBlockingErrors(true);
      this.lastBlockingErrors = this.getBlockingErrors();
      this.changeDetectorRef.detectChanges();
      openModal = !!this.newBlockingErrors.length;
    }
    if (openModal) {
      this.modalService.closeAllModals();
      this.changeDetectorRef.markForCheck();
      this.blockingErrorModalIsLaunched = true;
      this.modalService.openModalWithId('blockingErrorResolutionModal');
    }
  }

  public getBlockingErrors(compareToPreviousState?: boolean): BlockingError[] {
    return this.formGeneratorService.getBlockingErrors(
      this.getCurrentGlobalErrorGroup(),
      this.getCurrentGlobalErrorMessageGroup(),
      this.tabSheetID,
      this.descriptor.transId,
      compareToPreviousState ? this.lastBlockingErrors : undefined
    );
  }

  public getCurrentGlobalErrorGroup(): FormGeneratorError {
    return {...this.errors, ...this.zoneErrors};
  }

  public getCurrentGlobalErrorMessageGroup(): ErrorMessage[] {
    return [...this.errorMessages, ...this.zoneErrorMessages];
  }

  public addEntity() {
    this.isNewTableRow = true;
    const newData = this.formGeneratorService.createEmptyData(this.descriptor, undefined, false)[0];
    this.data.unshift(newData);
    this.reLoadRows();
  }

  public reLoadRows(rowIndex?: number, focusedCellId?: string) {
    this.loadRows(rowIndex, focusedCellId).then((group) => {
      setTimeout(() => {
        if (!isEqual(this.initialGroupValue, this.group.value)) {
          group.markAsDirty();
          this.changeDetectorRef.markForCheck();
        }
        if (this.isNewTableRow) {
          this.isNewTableRow = false;
        }
      });
    });
  }

  public allGroupZoneKeyHaveValue(groupValues) {
    if (groupValues) {
      return groupValues.every(value => !isValueConsideredAsEmpty(value[this.descriptor.groupZoneKeyName]));
    }
  }

  public timeLineValidation(): any {
    this.errors = {};
    this.errorText = null;
    this.errorMessages = [];
    let emptyRequiredZones = [];


    //  Check at least one data if MANDATORY
    if (this.formGeneratorService.isGroupZoneMandatory(this.descriptor.groupZoneType)) {
      if (this.isUndefinedDataEmpty()) {
        this.pushError(FormGeneratorErrorKind.ONE_LINE_REQUIRED);
      }
      // If NON_MANDATORY with required zones don't show required errors if the data has undefined id
      // and all values are empty
    } else {
      if (this.group && this.noDataForThisGroup()
        && this.atLeastOneRawNotEmpty()
        && this.requiredZones.length > 0) {

        this.requiredZones.forEach(key => {
          this.group.controls.forEach(control => {
            if (control['controls'][key.zoneID] && isValueConsideredAsEmpty(control['controls'][key.zoneID].value)
              && !emptyRequiredZones.includes(key.transId)) {
              emptyRequiredZones.push(key.transId);
            }
          });
        });

        if (emptyRequiredZones.length > 0) {
          this.translate.get(emptyRequiredZones).subscribe(value => {
            emptyRequiredZones = Object.values(value);
          });

          if (emptyRequiredZones.length === 1) {
            this.pushError(FormGeneratorErrorKind.KEY_REQUIRED, true, {key: emptyRequiredZones[0]});
          } else {
            this.pushError(FormGeneratorErrorKind.KEYS_REQUIRED, true, {keys: emptyRequiredZones.join(', ')});
          }
        }
      }
    }

    if (this.descriptor.groupZoneKeyName) {
      const keyZoneDescriptor = this.descriptor.zones
        .find(zone => zone.zoneID === this.descriptor.groupZoneKeyName);
      const keyTransId = this.translate.instant(keyZoneDescriptor.transId);
      // For groupZones with key the key is required
      // so show that error if one key is missing or if one other value is filled

      if (((this.group.value.length === 1 && !this.allValuesAreEmpty(this.group.value[0]))
          || this.group.value.length > 1)
        && !this.allGroupZoneKeyHaveValue(this.group.value)
        && keyZoneDescriptor.type !== ZoneType.AUTO_GENERATED) {

        // If you have to show the key required error and you have another zone that is required and empty you will
        // take the plural form 'keys-are-required' and if you already have the key-are-required error you will add
        // your key at this list of keys so you display one error for all keys

        if (this.errors[FormGeneratorErrorKind.KEY_REQUIRED]
          && !this.formGeneratorService.isGroupZoneMandatory(this.descriptor.groupZoneType)
        ) {
          this.errors[FormGeneratorErrorKind.KEY_REQUIRED].status = false;
          this.pushError(FormGeneratorErrorKind.KEYS_REQUIRED, true, {
            keys: keyTransId.concat(', ').concat(emptyRequiredZones.join(', '))
          });
        } else {

          if (this.errors[FormGeneratorErrorKind.KEYS_REQUIRED]) {
            this.errors[FormGeneratorErrorKind.KEYS_REQUIRED].additionalValues.keys
              = keyTransId.concat(', ').concat(emptyRequiredZones.join(', '));
          } else {
            this.pushError(FormGeneratorErrorKind.KEY_REQUIRED, true, {key: keyTransId});
          }

        }
      }

      const datasByKeyValue = groupBy(this.data, data => data.values[this.descriptor.groupZoneKeyName].value);
      for (const keyValue of Object.keys(datasByKeyValue)) {
        const datasOfKeyValue = datasByKeyValue[keyValue];
        let contractDates;
        if (this.descriptor.metadatas && this.descriptor.metadatas.dateRange) {
          contractDates = this.descriptor.metadatas.dateRange;
        }
        const check = this.formGeneratorService.getGapsOverlapsBoundaries(datasOfKeyValue);
        // For all MANDATORY, gaps in any kind are not allowed
        if (this.formGeneratorService.isGroupZoneMandatory(this.descriptor.groupZoneType)) {
          if (check.gaps > 0) {
            this.pushError(FormGeneratorErrorKind.GAPS_WITH_KEY, true, {keyValue: keyValue});
          }
          if (check.gapsToBegin > 0) {
            this.pushError(FormGeneratorErrorKind.DATED_BLOCK_FIRST_OCCURENCE_BEGIN_DATE_ETERNITY_WITH_KEY, true, {keyValue: keyValue});
          }
          if (check.gapsToEnd > 0) {
            this.pushError(FormGeneratorErrorKind.DATED_BLOCK_LAST_OCCURENCE_END_DATE_ETERNITY_WITH_KEY, true, {keyValue: keyValue});
          }
        }
        // For UNIQUE, overlaps are not allowed
        if (this.formGeneratorService.isGroupZoneUnique(this.descriptor.groupZoneType)) {
          if (datasOfKeyValue.length > 1 && !this.descriptor.isDated) {
            this.pushError(FormGeneratorErrorKind.ONLY_ONE_LINE_ALLOWED_BY_KEY, true, {
              key: keyTransId,
              keyValue: keyValue
            });
          }

          if (check.overlaps > 0) {
            this.pushError(FormGeneratorErrorKind.OVERLAPS_WITH_KEY, true, {keyValue: keyValue});
          }
        }
      }
    } else {
      // For MULTIPLE_MANDATORY, gaps in any kind are not allowed
      if (this.descriptor.groupZoneType === GroupZoneType.MULTIPLE_MANDATORY) {
        const check = this.formGeneratorService.getGapsOverlapsBoundaries(this.data);
        if (check.gaps > 0) {
          this.pushError(FormGeneratorErrorKind.GAPS);
        }
        if (check.gapsToBegin > 0) {
          this.pushError(FormGeneratorErrorKind.DATED_BLOCK_FIRST_OCCURENCE_BEGIN_DATE_ETERNITY);
        }
        if (check.gapsToEnd > 0) {
          this.pushError(FormGeneratorErrorKind.DATED_BLOCK_LAST_OCCURENCE_END_DATE_ETERNITY);
        }
      }
    }

    this.checkZoneErrors();

    Object.keys(this.errors).forEach(key => {
      if (!this.errors[key].status) {
        delete this.errors[key];
      } else {
        this.errorMessages.push({
          text: key,
          additionalValues: this.errors[key].additionalValues
        });
      }
    });

    if (!Object.keys(this.errors).length) {
      this.errors = null;
    } else {
      this.errorText = Object.keys(this.errors)[0];
    }

    return this.errors;
  }

  public isLastFieldFocused(fieldToCheck: ZoneDescriptor): boolean {
    return FormGeneratorUtils.sanitizeZoneName(fieldToCheck.zoneID) === FormGeneratorUtils
      .sanitizeZoneName(this.lastFieldFocused.descriptor.zoneID);
  }

  public getRowComponentInstanceFromIndex(index: number): FormTableRowComponent {
    return this.tableRows[index].instance;
  }

  public cancelChange(zoneDescriptor: ZoneDescriptor) {
    const lastEditedField: FormControlWithNativeElement =
      this.formGeneratorService.getZoneControlById(zoneDescriptor.zoneID, this.group, this.dataIndex);

    if (lastEditedField && this.isLastFieldFocused(zoneDescriptor)) {
      const rowInstance = this.getRowComponentInstanceFromIndex(this.dataIndex);

      lastEditedField.setValue(this.lastFieldFocused.value);

      if (lastEditedField.value === this.getZoneLastNonBlockingValue(zoneDescriptor.zoneID)) {
        lastEditedField.markAsPristine();
      }

    } else {
      throw new Error(`Last change on ${zoneDescriptor.zoneID} could not be canceled`);
    }
    this.blockingErrorModalIsLaunched = false;
  }

  public getZoneLastNonBlockingValue(zoneId: string): any {
    return this.lastNonBlockingData[this.dataIndex].values[zoneId].value;
  }

  public backToEditMode(zoneDescriptor: ZoneDescriptor) {
    const lastEditedField: FormControlWithNativeElement =
      this.formGeneratorService.getZoneControlById(zoneDescriptor.zoneID, this.group, this.dataIndex);

    if (lastEditedField) {
      const rowInstance = this.getRowComponentInstanceFromIndex(this.dataIndex);
      if (!rowInstance.editMode) {
        this.formGeneratorService.clickedTable$.next(rowInstance.cardDescriptor.groupZoneID);
        rowInstance.toggleCardEdit(true, true);
      }

      setTimeout(() => {
        if (lastEditedField.nativeElement) {
          lastEditedField.nativeElement.focus();
        } else {
          throw new Error(`lastEditField couldn't be focused because his nativeElement isn't accessible.
          Check if NativeElementInjectorDirective does work. ${lastEditedField}`);
        }
      }, 300);
    } else {
      throw new Error(`lastEditField couldn't be focused because the control couldn't be found`);
    }
    this.blockingErrorModalIsLaunched = false;
  }

  private setFieldEventsListeners() {

    // Store each time a field is focused
    if (!this.fieldFocusedSubscription) {
      this.fieldFocusedSubscription = this.formGeneratorService.fieldFocused$
        .pipe(
          filter(event => event !== null),
          filter(event => this.formGeneratorService.filterEventByZoneGroup(event, this.descriptor))
        )
        .subscribe((event: FieldEventTrace) => {
          this.handleFocusEvent(event);
        });
    }

    // Store each time a field is blurred
    if (!this.fieldBlurredSubscription) {
      this.fieldBlurredSubscription = this.formGeneratorService.fieldBlurred$
        .pipe(
          filter(event => event !== null),
          filter(event => this.formGeneratorService.filterEventByZoneGroup(event, this.descriptor))
        )
        .subscribe((event: FieldEventTrace) => {
          this.handleBlurEvent(event);
        });
    }
  }

  private removeFieldEventsListeners() {
    if (this.fieldFocusedSubscription) {
      this.fieldFocusedSubscription.unsubscribe();
      this.fieldFocusedSubscription = undefined;
    }
    if (this.fieldBlurredSubscription) {
      this.fieldBlurredSubscription.unsubscribe();
      this.fieldBlurredSubscription = undefined;
    }
  }

  private setTabChangeListener() {
    if (!this.tabChangeSubscription) {
      this.tabChangeSubscription = this.formGeneratorService.currentTab$
        .pipe(
          filter(event => event !== null)
        )
        .subscribe((tabId: string) => {
          this.handleTabChange(tabId);
        });
    }
  }

  // Avoid to listen events in hidden tabs
  private handleTabChange(tabId: string) {
    if (this.tabSheetID === tabId) {
      this.setFieldEventsListeners();
    } else {
      this.removeFieldEventsListeners();
    }
  }

  // Loop on zones to store errors
  private checkZoneErrors() {
    this.zoneErrors = {};
    this.zoneErrorMessages = [];

    if (this.group && this.group.controls) {
      this.group.controls.forEach(control => {
        Object.keys(control['controls']).forEach(controlKey => {
          if (control['controls'][controlKey].errors) {
            Object.keys(control['controls'][controlKey].errors).forEach(errorKey => {
              if (control['controls'][controlKey].errors[errorKey] === true) {
                const formattedErrorKey = this.formGeneratorService.formatErrorKey(errorKey);
                const zoneTransId = this.formGeneratorService.getTransIdFromZoneId(controlKey, this.descriptor);
                this.pushZoneError(formattedErrorKey, zoneTransId);
              }
            });
          }
        });
      });
    }

    Object.keys(this.zoneErrors).forEach(key => {
      if (!this.zoneErrors[key].status) {
        delete this.zoneErrors[key];
      } else {
        this.zoneErrorMessages.push({
          text: key,
          additionalValues: this.zoneErrors[key].additionalValues,
          zone: this.zoneErrors[key].zone
        });
      }
    });

  }

  private pushError(errorKey: string, status: boolean = true, additionalValues: any = {}) {
    this.errors[errorKey] = this.formGeneratorService.createError(errorKey, status, additionalValues);
  }

  private pushZoneError(errorKey: string, zone: string) {
    // add prefix because we can have several zones with same errorKey
    // TODO: refactor it with a more convenient data model
    this.zoneErrors[`${zone}/${errorKey}`] = this.formGeneratorService.createError(errorKey, true, {}, zone);
  }

  private sortByBeginDate() {
    this.data = this.data.sort((item1, item2) => {
      return moment(item2.begindate).startOf('day')
        .diff(moment(item1.begindate).startOf('day'), 'days');
    });
  }

  private sortByGroupZoneKeyName() {
    this.data = this.data.sort((data1, data2) => {
      const data1KeyValue = data1.values[this.descriptor.groupZoneKeyName].value || '';
      const data2KeyValue = data2.values[this.descriptor.groupZoneKeyName].value || '';
      const keyValueComparison = Strings.toString(data1KeyValue).localeCompare(Strings.toString(data2KeyValue));
      if (keyValueComparison === 0) {
        if (this.descriptor.isDated) {
          if (moment(data1.enddate).isAfter(moment(data2.enddate))) {
            return -1;
          } else if (moment(data1.enddate).isBefore(moment(data2.enddate))) {
            return 1;
          } else {
            return 0;
          }
        } else {
          return 0;
        }
      }
      return keyValueComparison;
    });
  }

  private sort() {
    if (this.descriptor.groupZoneKeyName) {
      // can't sort in a gz with key
      return;
    }

    let key = this.sortKey[0].key;
    if (key !== 'begindate' && key !== 'enddate') {
      key = `values.${key}.value`;
    }
    if (!this.selectKey) {
      this.data = orderBy(this.data, [key], ['asc']);
      this.selectKey = true;
    } else {
      this.data = orderBy(this.data, [key], ['desc']);
      this.selectKey = false;
    }
  }

  private loadRows(rowIndex?: number, focusedCellId?: string) {
    const canDelete = this.data.length > 1;

    this.ngOnDestroy();

    if (this.isFirstLoad && !this.descriptor.groupZoneKeyName) {
      this.sortByBeginDate();
      this.isFirstLoad = false;
    } else if (this.descriptor.groupZoneKeyName) {
      this.sortByGroupZoneKeyName();
      this.isFirstLoad = false;
    }

    const promisesOfLoadedObservables = this.data
      .map((data, index) => {
        data.dataIndex = index;

        const rowRef: ComponentRef<FormTableRowComponent> = this.tableRowsContainer.createComponent(
          this.componentFactoryResolver.resolveComponentFactory(FormTableRowComponent));

        this.changeDetectorRef.markForCheck();
        const row: FormTableRowComponent = rowRef.instance;
        this.tableRows.push(rowRef);

        row.cardDescriptor = this.descriptor;
        row.cellDescriptors = this.descriptor.zones;
        row.data = data;
        row.dataIndex = index;
        row.readOnly = this.readOnly;
        row.currency = this.currency;
        row.locale = this.locale;
        row.wasEditModeBeforeReload = rowIndex === index;
        row.isNewTableRow = index === 0 ? this.isNewTableRow : false;
        row.removeEntity = this.removeEntity.bind(this);
        row.cloneEntity = this.cloneEntity.bind(this);
        row.timeLineValidation = this.timeLineValidation.bind(this);
        row.setBackgroundColor(index % 2 === 0 ? '#f1f4f8' : null);
        row.reLoadRows = this.reLoadRows.bind(this);
        row.canDelete = canDelete;
        row.focusedCellId = focusedCellId;
        row.tableData = this.data;
        row.isOnBoarding = this.isOnBoarding;
        row.employeeId = this.employee?.id;

        if (this.descriptor.groupZoneKeyName) {
          // force keycell to be the first one
          row.cellDescriptors.sort((descriptor1, descriptor2) => {
            if (descriptor1.zoneID === this.descriptor.groupZoneKeyName) {
              return -1;
            }
            return descriptor1.displayOrder - descriptor2.displayorder;
          });
          row.groupZoneKeyName = this.descriptor.groupZoneKeyName;
          const groupZoneKeyValue = data.values[this.descriptor.groupZoneKeyName].value;

          const datasByGroupZoneKeyValue = this.data
            .filter(current => current.values[this.descriptor.groupZoneKeyName].value === groupZoneKeyValue);
          row.datasByGroupZoneKeyValue = datasByGroupZoneKeyValue;

          if (datasByGroupZoneKeyValue.length === 1) {
            row.isGroupCollapserVisible = data.id || data.values[this.descriptor.groupZoneKeyName].value;
            row.isGroupCollapserDisabled = true;
          } else {
            if (this.descriptor.isDated) {
              const dataWithMaxEndDate = datasByGroupZoneKeyValue
                .reduce((max, current) => moment(current.enddate).isAfter(max.enddate) ? current : max);
              row.isGroupCollapserVisible = dataWithMaxEndDate.dataIndex === data.dataIndex;
            } else {
              const dataWithMaxUpdateDate = datasByGroupZoneKeyValue
                .reduce((max, current) => moment(current.updatedate).isAfter(max.updatedate) ? current : max);
              row.isGroupCollapserVisible = dataWithMaxUpdateDate.dataIndex === data.dataIndex;
            }
          }

          if (row.isGroupCollapserVisible && !row.isGroupCollapserDisabled) {
            row.expandCollapseGroup = this.expandCollapseGroup.bind(this);
          }
        }

        row.onActiveRow.subscribe((dataIndex: number) => {
          this.dataIndex = dataIndex;
          this.changeDetectorRef.markForCheck();
        });

        return row.loaded.asObservable().toPromise();
      });

    return Promise.all(promisesOfLoadedObservables).then((rowGroups: any) => {

      rowGroups.forEach((group, index) => {
        // this.group.reset();
        this.group.setControl(index, group);
      });

      this.changeDetectorRef.markForCheck();
      this.groupValueChangesSubscription = this.group.valueChanges
        .subscribe(() => {
          if (this.group.dirty) {
            if (isEqual(this.initialGroupValue, this.group.value)) {
              this.group.markAsPristine();
              this.changeDetectorRef.markForCheck();
            }
          }
        });

      this.setTabChangeListener();

      // todo: If new entity add blockingError... ?
      this.newBlockingErrors = this.getBlockingErrors(true);
      if (this.newBlockingErrors.length) {
        // ...
      } else {
        this.lastNonBlockingData = cloneDeep(this.data);
      }
      this.group?.updateValueAndValidity();
      return this.group;
    });
  }

  private removeEntity(index: number, event) {
    event.stopPropagation();

    this.modalService.dynamicConfirmModal({
      title: 'DIALOG.delete_block_occurrence',
      text: this.translate.instant('DIALOG.delete_block_occurrence?', {blockName: this.descriptor.transId}),
      confirmButton: 'FORMS.delete_occurrence',
      cancelButton: 'FORMS.cancel'
    }).then((choice: boolean) => {
      if (choice) {
        if (this.data.length < 2) {
          this.data[0] = this.formGeneratorService.createEmptyData(this.descriptor)[0];
        } else {
          this.data.splice(index, 1);
          this.group.removeAt(index);
        }
        this.loadRows()
          .then((group) =>
            setTimeout(() => {
              if (isEqual(this.initialGroupValue, group.value)) {
                group.markAsPristine();
              } else {
                group.markAsDirty();
              }
              this.changeDetectorRef.markForCheck();
            })
          );
      }
    });
  }

  private cloneEntity(index: number, event) {
    event.stopPropagation();

    const newEntry = this.formGeneratorService.createEmptyData(this.descriptor)[0];
    const activeEntity = this.data[index];

    for (const key in newEntry.values) {

      if (activeEntity.values[key].type === 'FILE') {
        continue;
      }
      newEntry.values[key].value = activeEntity.values[key].value;
      newEntry.values[key].transId = activeEntity.values[key].transId;
    }
    newEntry.begindate = activeEntity.begindate;
    newEntry.enddate = activeEntity.enddate;
    this.data.unshift(newEntry);
    this.reLoadRows();
  }

  private getEarliestLatestDates(): any {
    const dates = {
      earliest: this.data[0].begindate,
      latest: this.data[0].enddate
    };

    this.data.forEach(item => {
      if (moment(item.begindate).isBefore(moment(dates.earliest))) {
        dates.earliest = item.begindate;
      }
      if (moment(item.enddate).isAfter(moment(dates.latest))) {
        dates.latest = item.enddate;
      }
    });
    return dates;
  }

  private expandCollapseGroup(index: number, event) {
    event.stopPropagation();

    const clickedData = this.data[index];
    const clickedDataKeyValue = clickedData.values[this.descriptor.groupZoneKeyName].value;
    const clickedTableRow = this.tableRows.find(row =>
      row.instance.data.dataIndex === clickedData.dataIndex
      && row.instance.data.values[this.descriptor.groupZoneKeyName].value === clickedDataKeyValue);
    const isGroupWillBeCollapsed = !clickedTableRow.instance.isGroupCollapserCollapsed;

    clickedTableRow.instance.isGroupCollapserCollapsed = isGroupWillBeCollapsed;

    this.tableRows
      .filter(row => row.instance.data.dataIndex !== clickedData.dataIndex)
      .filter(row => row.instance.data.values[this.descriptor.groupZoneKeyName].value === clickedDataKeyValue)
      .forEach(row => row.instance.setDisplay(isGroupWillBeCollapsed ? 'none' : 'table-row'));
  }

  private allValuesAreEmpty(obj) {
    if (obj) {
      const newObject = cloneDeep(obj);
      if (('begindate' in obj) && ('enddate' in obj)) {
        delete newObject['begindate'];
        delete newObject['enddate'];
      }
      const objectValues = Object.values(newObject);
      return objectValues
        .every(value => isValueConsideredAsEmpty(value));
    }
  }

  private noDataForThisGroup() {
    return this.data.every(data => !data.id);
  }

  private atLeastOneRawNotEmpty() {
    return this.group.value.some(value => !this.allValuesAreEmpty(value));
  }

  private allRowsValuesAreEmpty() {
    return this.group.value.every(value => this.allValuesAreEmpty(value));
  }

  private isUndefinedDataEmpty() {
    return this.group && this.noDataForThisGroup() && this.allRowsValuesAreEmpty();
  }

}
